/**
 * Retorno ao usuário: avisos no canto (toasts) e confirmação com janela
 * própria (substitui o confirm() do navegador).
 */
import { useEffect, useState } from "react";
import { create } from "zustand";
import { Icon } from "./icons";

interface Toast { id: number; text: string; kind: "success" | "error" }
interface ToastState { items: Toast[]; push: (t: Omit<Toast, "id">) => void; drop: (id: number) => void }

let seq = 0;
export const useToasts = create<ToastState>((set) => ({
  items: [],
  push: (t) => {
    const id = ++seq;
    set((s) => ({ items: [...s.items.slice(-3), { ...t, id }] }));
    setTimeout(() => set((s) => ({ items: s.items.filter((x) => x.id !== id) })), t.kind === "error" ? 6000 : 2800);
  },
  drop: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

export const toast = {
  ok: (text: string) => useToasts.getState().push({ text, kind: "success" }),
  error: (text: string) => useToasts.getState().push({ text, kind: "error" }),
};

export function Toaster(): JSX.Element {
  const { items, drop } = useToasts();
  return (
    <div className="toasts">
      {items.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`} onClick={() => drop(t.id)}>
          <span className="ok"><Icon name={t.kind === "success" ? "tick" : "x"} /></span>{t.text}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------- confirmação

interface Ask { text: string; confirm?: string; danger?: boolean; resolve: (ok: boolean) => void }
const useAsk = create<{ ask: Ask | null; set: (a: Ask | null) => void }>((set) => ({ ask: null, set: (ask) => set({ ask }) }));

/** Há uma confirmação aberta na tela? (as janelas ignoram Esc enquanto isso) */
export const confirmOpen = (): boolean => !!useAsk.getState().ask;

/** await confirmar("Excluir?") → true/false */
export function confirmar(text: string, opts: { confirm?: string; danger?: boolean } = {}): Promise<boolean> {
  return new Promise((resolve) => useAsk.getState().set({ text, ...opts, resolve }));
}

export function ConfirmHost(): JSX.Element | null {
  const { ask, set } = useAsk();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ask) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { ask.resolve(false); set(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ask, set]);
  if (!ask) return null;
  const close = (ok: boolean) => { setBusy(false); ask.resolve(ok); set(null); };
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(false); }}>
      <div className="modal" style={{ maxWidth: 440 }} role="alertdialog">
        <div className="modal-body" style={{ paddingTop: 22 }}>{ask.text}</div>
        <div className="modal-foot">
          <button className="btn" onClick={() => close(false)}>Cancelar</button>
          <button className={`btn ${ask.danger ? "danger" : "primary"}`} disabled={busy} autoFocus onClick={() => { setBusy(true); close(true); }}>{ask.confirm ?? "Confirmar"}</button>
        </div>
      </div>
    </div>
  );
}
